/**
 * Contact Management API Service
 * Handles Customer and Supplier CRUD operations with proper error handling
 */

import config from "../../config";

const getAccountId = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.id || user.accountId;
};

const buildUrl = (path, params = {}) => {
    const filtered = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    );
    const qs = new URLSearchParams({ account_id: getAccountId(), ...filtered });
    return `${config.apiUrl}/${path}?${qs}`;
};

// ════════════════════════════════════════════════════════════
// CUSTOMERS API
// ════════════════════════════════════════════════════════════

export const getCustomers = async (p = {}) => {
    const res = await fetch(buildUrl("getCustomers", p));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getCustomerKpis = async () => {
    const res = await fetch(buildUrl("getCustomerKpis"));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getCustomerById = async (id) => {
    const res = await fetch(buildUrl("getCustomerById", { id }));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getCustomerCompanyNames = async () => {
    const res = await fetch(buildUrl("getCustomers", { pageSize: 1000, page: 1 }));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { data } = await res.json();
    return [...new Set((data || []).map((r) => r.name).filter(Boolean))].sort();
};

export const createCustomer = async (data) => {
    const res = await fetch(`${config.apiUrl}/createCustomer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), ...data }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const updateCustomer = async (id, data) => {
    const res = await fetch(`${config.apiUrl}/updateCustomer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), id, ...data }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const deleteCustomer = async (id) => {
    const res = await fetch(buildUrl("deleteCustomer", { id }), { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ════════════════════════════════════════════════════════════
// SUPPLIERS API
// ════════════════════════════════════════════════════════════

export const getSuppliers = async (p = {}) => {
    const res = await fetch(buildUrl("getSuppliers", p));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getSupplierKpis = async () => {
    const res = await fetch(buildUrl("getSupplierKpis"));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getSupplierById = async (id) => {
    const res = await fetch(buildUrl("getSupplierById", { id }));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getSupplierCompanyNames = async () => {
    const res = await fetch(buildUrl("getSuppliers", { pageSize: 1000, page: 1 }));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { data } = await res.json();
    return [...new Set((data || []).map((r) => r.name).filter(Boolean))].sort();
};

export const createSupplier = async (data) => {
    const res = await fetch(`${config.apiUrl}/createSupplier`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), ...data }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const updateSupplier = async (id, data) => {
    const res = await fetch(`${config.apiUrl}/updateSupplier`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), id, ...data }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const deleteSupplier = async (id) => {
    const res = await fetch(buildUrl("deleteSupplier", { id }), { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ════════════════════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════════════════════

export const INCOTERMS_LIST = [
    "EXW – Ex Works",
    "FCA – Free Carrier",
    "FAS – Free Alongside Ship",
    "FOB – Free On Board",
    "CFR – Cost and Freight",
    "CIF – Cost, Insurance and Freight",
    "CPT – Carriage Paid To",
    "CIP – Carriage and Insurance Paid To",
    "DAP – Delivered At Place",
    "DPU – Delivered at Place Unloaded",
    "DDP – Delivered Duty Paid",
];

export const COMMODITY_LIST = [
    "Aluminium", "Steel", "Copper", "Plastics", "ABS Resin",
    "PCB", "Electronics", "Cables", "Rubber", "Gaskets",
    "Bearings", "Fasteners", "Paint", "Chemicals", "Solvents",
    "Timber", "Glass", "Packaging", "Cartons", "Lubricants",
    "Sensors", "Valves", "Adhesives", "Welding Gas", "Logistics",
];

export const AREAS = [];
export const STATUSES = ["All", "Active", "Inactive"];
export const SEARCH_TYPES = ["all", "id", "name", "contact", "email", "phone", "area"];